import {JsonDataType} from '../lib/json';

export type AccountGridDataType = 'text' | 'date' | 'datetime' | 'boolean' | 'number' | 'integer' | 'amount' | 'percentage';

export type AccountGridGroup = 'core' | 'tiering' | 'kpi' | 'tracking-expiring' | 'tracking-renewal' | 'output' | 'history';
export type DataMapKeyType = 'country' | 'currency' | 'YesNo' | 'RenewableFlag' | 'RenewalStatus' | 'TieringFlag' | 'Methodology' | 'LeadFollow';
export type AccountAttrAlignment = 'left' | 'center' | 'right';

export interface AccountDataAttribute {
    attribute: string;
    name: string;
    group?: AccountGridGroup;
    pfmDataType: AccountGridDataType;
    dataMapKey?: DataMapKeyType;
    isReadOnly?: boolean;
    format?: string;
    align?: AccountAttrAlignment;
    width?: number | string;
}

export interface KpiDefinition {
    kpiKey: string;
    lobKey: string;
    attribute: string;
    position: number | null;
    name: string;
    definition: string;
    weight: number | null;
    inputValue: string;
    directionAscending: boolean;
    interpretation: string | null;
    isDiscrete: boolean;
    format?: string;
}

export interface KpiWithDescription {
    id?: string | null;
    attribute: string;
    lobKey: string;
    lobName: string;
    officeKey: string;
    officeName: string;
    weight: number;
    directionUp: boolean;
    directionIcon: string;
    description: string;
    name: string;
    definition: string;
    inputValue: string;
    interpretation: string;
    position?: number;
    isDiscrete: string;
}

const AccountDataDictionaryRaw: AccountDataAttribute[] = [
    { attribute: 'editedBy', name: 'Edited By', group: 'history', pfmDataType: 'text' },
    { attribute: 'editedAt', name: 'Edited At', group: 'history', pfmDataType: 'datetime' },
    { attribute: 'version', name: 'Version', group: 'history', pfmDataType: 'integer' },
    { attribute: 'id', name: 'ID', group: 'history', pfmDataType: 'text' },
    { attribute: 'status', name: 'Status', group: 'core', pfmDataType: 'text', width: 35, align: 'center', isReadOnly: true },
    { attribute: 'row', name: 'Row', group: 'core', pfmDataType: 'integer', format: 'n0', width: 35, align: 'center', isReadOnly: true },
    { attribute: 'errorLevel', name: 'DQ', group: 'core', pfmDataType: 'text', width: 30, align: 'center', isReadOnly: true },
    { attribute: 'crmSubmissionId', name: 'Old Submission ID', group: 'core', pfmDataType: 'text' },
    //{ attribute: 'agoraId', name: 'AGORA ID', group: 'group1', pfmDataType: 'text' },
    { attribute: 'uwbId', name: 'AGORA ID', group: 'core', pfmDataType: 'text', width: 120 },
    { attribute: 'masterPolicy', name: 'Master Policy Number', group: 'core', pfmDataType: 'text' },
    { attribute: 'policyId', name: 'Policy Number', group: 'core', pfmDataType: 'text' },
    { attribute: 'uwy', name: 'UWY', group: 'core', pfmDataType: 'integer', width: 50 },
    // { attribute: 'techExId', name: 'TechEx ID', group: 'core', pfmDataType: 'text' },
    { attribute: 'account', name: 'Client', group: 'core', pfmDataType: 'text', width: 120 },
    { attribute: 'broker', name: 'Broker', group: 'core', pfmDataType: 'text', width: 120 },
    { attribute: 'underwriter', name: 'Underwriter', group: 'core', pfmDataType: 'text', width: 120 },
    { attribute: 'product', name: 'Product / Cover Package', group: 'core', pfmDataType: 'text' },
    { attribute: 'country', name: 'Country', group: 'core', pfmDataType: 'text', dataMapKey: 'country' },
    { attribute: 'inceptionDate', name: 'Inception Date', group: 'core', pfmDataType: 'date' },
    { attribute: 'expiryDate', name: 'Expiration Date', group: 'core', pfmDataType: 'date' },
    { attribute: 'gwp', name: 'GWP - Input Currency (our share)', group: 'core', pfmDataType: 'amount', format: 'n0' },
    { attribute: 'gwpEur', name: 'GWP - EUR (our share)', group: 'core', pfmDataType: 'amount', isReadOnly: true, format: 'n0' },
    { attribute: 'gnwpEur', name: 'GNWP - EUR (our share)', group: 'core', pfmDataType: 'amount', format: 'n0' },
    { attribute: 'prevPilStatus', name: 'Previous PIL Status', group: 'core', pfmDataType: 'text', dataMapKey: 'TieringFlag', width: 90 },
    { attribute: 'keyAccount', name: 'Key Account', group: 'core', pfmDataType: 'boolean', /*dataMapKey: 'YesNo',*/ width: 50 },
    { attribute: 'renewableFlag', name: 'Renewable', group: 'core', pfmDataType: 'boolean', /*dataMapKey: 'RenewableFlag'*/ width: 50 },
    { attribute: 'targetRateChangeKeyAccounts', name: 'Target Rate Change (for key accounts)', group: 'core', pfmDataType: 'percentage' },
    { attribute: 'retentionRateKeyAccounts', name: 'Retention Rate (for key accounts)', group: 'core', pfmDataType: 'percentage' },
    { attribute: 'multinationalFlag', name: 'Mono / Multinational', group: 'core', pfmDataType: 'text', dataMapKey: 'YesNo', width: 55 },
    { attribute: 'portfolioWeight', name: 'Portfolio Weight', group: 'output', pfmDataType: 'percentage', isReadOnly: true, format: 'p1' },
    { attribute: 'bucketWeight', name: 'Bucket Weight', group: 'output', pfmDataType: 'percentage', isReadOnly: true },
    { attribute: 'customNotes', name: 'Custom notes', group: 'output', pfmDataType: 'text' },
    { attribute: 'manualPilStatus', name: 'Manual PIL status', group: 'core', pfmDataType: 'text', dataMapKey: 'TieringFlag', width: 90 },
    { attribute: 'rationaleChange', name: 'Rationale for change', group: 'core', pfmDataType: 'text' },
    { attribute: 'crmRenewalId', name: 'Submission ID', group: 'tracking-renewal', pfmDataType: 'text' },
    { attribute: 'uwbRenewalId', name: 'AGORA ID', group: 'core', pfmDataType: 'text' },
    { attribute: 'renewalUnderwriter', name: 'Underwriter', group: 'tracking-renewal', pfmDataType: 'text' },
    { attribute: 'renewalStatus', name: 'Status', group: 'tracking-renewal', pfmDataType: 'text', dataMapKey: 'RenewalStatus' },
    { attribute: 'renewalGwp', name: 'GWP - Input Currency (our share)', group: 'tracking-renewal', pfmDataType: 'amount' },
    { attribute: 'renewalGnwp', name: 'GNWP - Input Currency (our share)', group: 'tracking-renewal', pfmDataType: 'amount' },
    { attribute: 'renewalCurrency', name: 'Currency', group: 'tracking-renewal', pfmDataType: 'text', dataMapKey: 'currency' },
    { attribute: 'renewalGwpEur', name: 'GWP - EUR (our share)', group: 'tracking-renewal', pfmDataType: 'amount', isReadOnly: true  },
    { attribute: 'renewalGnwpEur', name: 'GNWP - EUR (our share)', group: 'tracking-renewal', pfmDataType: 'amount', isReadOnly: true },
    { attribute: 'renewalInceptionDate', name: 'Inception Date', group: 'tracking-renewal', pfmDataType: 'date' },
    { attribute: 'renewalExpiryDate', name: 'Expiration Date', group: 'tracking-renewal', pfmDataType: 'date' },
    { attribute: 'renewalRunningDays', name: 'Running Days', group: 'tracking-renewal', pfmDataType: 'amount',  isReadOnly: true, width: 60 },
    { attribute: 'renewalAgoraStatus', name: 'AGORA Status', group: 'tracking-renewal', pfmDataType: 'text',  isReadOnly: true, width: 80 },
    { attribute: 'renewalAgoraSubStatus', name: 'AGORA Substatus', group: 'tracking-renewal', pfmDataType: 'text',  isReadOnly: true, width: 80 },
    { attribute: 'methodology', name: 'Methodology', group: 'tracking-expiring', pfmDataType: 'text', dataMapKey: 'Methodology', width: 130 },
    //{ attribute: 'aptp', name: 'Expiring APTP', group: 'group4', pfmDataType: 'percentage', format: 'p1' },
    { attribute: 'expiringShare', name: 'Share %', group: 'tracking-expiring', pfmDataType: 'percentage' },
    { attribute: 'expiringCurrency', name: 'Currency', group: 'tracking-expiring', pfmDataType: 'text', dataMapKey: 'currency' },
    { attribute: 'expiringExposure', name: 'Exposure (100%)', group: 'tracking-expiring', pfmDataType: 'amount' },
    { attribute: 'expiringDeductible', name: 'Deductible (100%)', group: 'tracking-expiring', pfmDataType: 'amount' },
    { attribute: 'expiringLimit', name: 'Limit (100%)', group: 'tracking-expiring', pfmDataType: 'amount' },
    { attribute: 'renewalAptp', name: 'APTP', group: 'tracking-renewal', pfmDataType: 'percentage' },
    { attribute: 'renewalShare', name: 'Share %', group: 'tracking-renewal', pfmDataType: 'percentage' },
    { attribute: 'renewalExposure', name: 'Exposure (100%)', group: 'tracking-renewal', pfmDataType: 'amount' },
    { attribute: 'renewalDeductible', name: 'Deductible (100%)', group: 'tracking-renewal', pfmDataType: 'amount' },
    { attribute: 'renewalLimit', name: 'Limit (100%)', group: 'tracking-renewal', pfmDataType: 'amount' },
    { attribute: 'purePremiumChange', name: 'Pure Premium Change', group: 'output', pfmDataType: 'percentage', isReadOnly: true },
    { attribute: 'repricingFacilityErc', name: 'Repricing Facility ERC (Pricing Tool)', group: 'tracking-renewal', pfmDataType: 'percentage' },
    { attribute: 'manualErc', name: 'Manual ERC (Pricing Tool)', group: 'tracking-renewal', pfmDataType: 'percentage' },
    { attribute: 'comments', name: 'Comments', group: 'tracking-renewal', pfmDataType: 'text' },
    { attribute: 'manualOverride', name: 'Manual Override', group: 'tracking-renewal', pfmDataType: 'percentage' },
    { attribute: 'scoreAts', name: 'Score (ATS)', group: 'output', pfmDataType: 'percentage', isReadOnly: true },
    { attribute: 'calculatedPilStatus', name: 'Calculated PIL status', group: 'output', pfmDataType: 'text', dataMapKey: 'TieringFlag', width: 90, isReadOnly: true },
    { attribute: 'finalPilStatus', name: 'Final PIL status', group: 'output', pfmDataType: 'text', dataMapKey: 'TieringFlag', width: 90, isReadOnly: true },
    { attribute: 'targetRateChange', name: 'Target Rate Change', group: 'output', pfmDataType: 'percentage', isReadOnly: true },
    { attribute: 'retainedGnwp', name: 'Retained GNWP', group: 'output', pfmDataType: 'amount', isReadOnly: true },
    { attribute: 'riskFactors', name: 'Risk Factors', group: 'output', pfmDataType: 'percentage', isReadOnly: true },
    { attribute: 'asIfPremiumExpired', name: 'As-If Premium Expired', group: 'output', pfmDataType: 'amount', isReadOnly: true },
    { attribute: 'effectiveRateChange', name: 'Effective Rate Change', group: 'output', pfmDataType: 'percentage', isReadOnly: true },
    { attribute: 'retentionRate', name: 'Retention Rate', group: 'output', pfmDataType: 'percentage', isReadOnly: true },

    { attribute: 'leadFollow', name: 'Lead/Follow', group: 'kpi', pfmDataType: 'text', dataMapKey: 'LeadFollow' },
    { attribute: 'hazardGrade', name: 'Hazard Grade', group: 'kpi', pfmDataType: 'number' },
    { attribute: 'historicalIlr', name: 'Historical ILR', group: 'kpi', pfmDataType: 'percentage', format: 'p2' },

    { attribute: 'aptp', name: 'APTP', group: 'kpi', pfmDataType: 'percentage', format: 'p1' },
    { attribute: 'historicalUlr', name: 'Historical ULR*', group: 'kpi', pfmDataType: 'percentage', format: 'p2' },
    { attribute: 'percentUsExposure', name: '% US Exposure', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'primaryExcess', name: 'Primary/Excess', group: 'kpi', pfmDataType: 'number', format: 'g0' },
    { attribute: 'triggerProductsLiability', name: 'Trigger Products Liability', group: 'kpi', pfmDataType: 'number', format: 'g0' },
    { attribute: 'socratesScore', name: 'Socrates Score', group: 'kpi', pfmDataType: 'integer' },
    { attribute: 'piPrimaryExcess', name: 'Primary/Excess', group: 'kpi', pfmDataType: 'number', format: 'g0' },
    { attribute: 'piAptp', name: 'PI APTP ', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'piHistoricalUlr', name: 'PI Historical ULR*', group: 'kpi', pfmDataType: 'percentage', format: 'p2' },
    { attribute: 'piHazardGrade', name: 'PI Hazard Grade', group: 'kpi', pfmDataType: 'integer' },
    { attribute: 'piUsExposure', name: 'PI US Exposure ', group: 'kpi', pfmDataType: 'amount' },
    { attribute: 'doHistoricalUlr', name: 'DO Historical ULR*', group: 'kpi', pfmDataType: 'percentage', format: 'p2' },
    { attribute: 'doAptp', name: 'DO APTP ', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'doHazardGrade', name: 'DO Hazard Grade', group: 'kpi', pfmDataType: 'integer' },
    { attribute: 'doPeuExposure', name: 'DO PEU Exposure', group: 'kpi', pfmDataType: 'amount' },
    { attribute: 'doPercentUsMarketCap', name: 'DO % US market cap.', group: 'kpi', pfmDataType: 'amount' },
    { attribute: 'dnvHull', name: 'DNV (Hull)', group: 'kpi', pfmDataType: 'amount' },
    { attribute: 'aalOnPremiumCargo', name: 'AAL on Premium (Cargo)', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'expectedLossRatio', name: 'Expected Loss Ratio', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'largeFrequency', name: 'Frequency', group: 'kpi', pfmDataType: 'number', format: 'p2' },
    { attribute: 'largeLossMargin', name: 'LL Margin', group: 'kpi', pfmDataType: 'number', format: 'p2' },
    { attribute: 'adjustedVolatility', name: 'Volatility', group: 'kpi', pfmDataType: 'number', format: 'n5' },
    { attribute: 'diversification', name: 'Diversification', group: 'kpi', pfmDataType: 'number', format: 'n4' },
    { attribute: 'aalOnPremium', name: 'AAL on Premium', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'oarr', name: 'OARR', group: 'kpi', pfmDataType: 'number', format: 'n2' },
    { attribute: 'facReinsurance', name: 'FAC Reinsurance', group: 'kpi', pfmDataType: 'amount' },
    { attribute: 'multilineRelationship', name: 'Multiline relationship', group: 'kpi', pfmDataType: 'text' },
    { attribute: 'overallClientGcCHistoricalIlr', name: 'Overall Client GC&C Historical ILR', group: 'kpi', pfmDataType: 'percentage', format: 'p2' },
    { attribute: 'historicalIlr2yrs', name: 'Historical ILR 2yrs', group: 'kpi', pfmDataType: 'percentage', format: 'p2' },
    { attribute: 'mflTivRatio', name: 'MFL / TIV ratio', group: 'kpi', pfmDataType: 'percentage' },
    { attribute: 'overallHistoricalIlr', name: 'Overall Client GC&C Historical ILR', group: 'kpi', pfmDataType: 'percentage' },

    { attribute: 'createdBy', name: 'Created By', group: 'history', pfmDataType: 'text' },
    { attribute: 'createdAt', name: 'Created At', group: 'history', pfmDataType: 'datetime' },
    { attribute: 'updatedBy', name: 'Updated By', group: 'history', pfmDataType: 'text' },
    { attribute: 'updatedAt', name: 'Updated At', group: 'history', pfmDataType: 'datetime' },

    { attribute: 'legacyAptp', name: 'Legacy APTP', group: 'tracking-renewal', pfmDataType: 'percentage' },
];

const KpiDefinitionsRaw: KpiDefinition[] = [
    {
        kpiKey: 'avn_lead_follow',
        lobKey: 'AVN',
        attribute: 'leadFollow',
        position: 1,
        name: 'Lead/Follow',
        definition: 'Binary numeric indicator for Lead vs Follow',
        weight: 0.2,
        inputValue: '0 or 1, where 0 = follow  and 1 = lead, and 0 is less adequate than 1',
        directionAscending: true,
        interpretation: 'Lead accounts will obtain a higher Account Tiering Score (ATS)',
        isDiscrete: true
    },
    {
        kpiKey: 'avn_hazard_grade',
        lobKey: 'AVN',
        attribute: 'hazardGrade',
        position: 2,
        name: 'Hazard Grade',
        definition: 'Hazard Grade ',
        weight: 0.3,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Accounts with higher HG will obtain a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'avn_historical_ilr',
        lobKey: 'AVN',
        attribute: 'historicalIlr',
        position: 3,
        name: 'Historical ILR',
        definition: 'Historical Incurred Loss Ratio. Aggregate GN incurred LR over the last 5 or 10 UW years',
        weight: 0.5,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with higher Historical LR will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'cas_aptp',
        lobKey: 'CAS',
        attribute: 'aptp',
        position: 1,
        name: 'APTP',
        definition: 'Actual Price/Technical Price from pricing model',
        weight: 0.25,
        inputValue: 'Percentage',
        directionAscending: true,
        interpretation: 'Accounts with higher APTP  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'cas_hazard_grade',
        lobKey: 'CAS',
        attribute: 'hazardGrade',
        position: 2,
        name: 'Hazard Grade',
        definition: 'Hazard Grade ',
        weight: 0.15,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Accounts with higher HG will obtain a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'cas_historical_ulr_',
        lobKey: 'CAS',
        attribute: 'historicalUlr',
        position: 3,
        name: 'Historical ULR*',
        definition: 'Historical Ultimate Loss Ratio. Aggregate GN Ultimate LR over the last 5 or 10 UW years ',
        weight: 0.25,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with a higher ULR or ILR will obtain a lower ATS. *ULR when reliable, otherwise ILR. ',
        isDiscrete: false
    },
    {
        kpiKey: 'cas_us_exposure',
        lobKey: 'CAS',
        attribute: 'percentUsExposure',
        position: 4,
        name: '% US Exposure',
        definition: 'Percentage of US exposure',
        weight: 0.15,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with a higher % of US exposure will obtain a lower ATS. % US exposure aims to favor the low US exposure. ',
        isDiscrete: false
    },
    {
        kpiKey: 'cas_primary_excess',
        lobKey: 'CAS',
        attribute: 'primaryExcess',
        position: 5,
        name: 'Primary/Excess',
        definition: 'Binary numeric indicator for primary vs excess',
        weight: 0.1,
        inputValue: '0 or 1, where 0 = Primary and 1 = Excess, and 0 is more  adequate than 1',
        directionAscending: false,
        interpretation: 'Primary accounts will obtain a higher ATS. Primary/Excess aims to favor the primary business.',
        isDiscrete: true
    },
    {
        kpiKey: 'cas_trigger_products_liability',
        lobKey: 'CAS',
        attribute: 'triggerProductsLiability',
        position: 6,
        name: 'Trigger Products Liability',
        definition: 'Binary numeric indicator for Occurrence vs Claims Made',
        weight: 0.1,
        inputValue: '0 or 1, where 0 = Claims and 1 = Occurrence, and 0 is more  adequate than 1',
        directionAscending: false,
        interpretation: 'Trigger Products Liability aims to prioritize accounts where the liability is triggered by claims, that will receive a higher ATS. ',
        isDiscrete: true
    },
    {
        kpiKey: 'cyb_socrates_score',
        lobKey: 'CYB',
        attribute: 'socratesScore',
        position: 1,
        name: 'Socrates Score',
        definition: 'Socrates Score ranging from 0 to 100',
        weight: 0.25,
        inputValue: 'From 0 to 100',
        directionAscending: true,
        interpretation: 'Accounts with a higher SS will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'cyb_primary_excess',
        lobKey: 'CYB',
        attribute: 'piPrimaryExcess',
        position: 2,
        name: 'Primary/Excess',
        definition: 'Binary numeric indicator for attachment point below € 50 million and above € 50 million. ',
        weight: 0.25,
        inputValue: '0 or 1, where 1 >=  € 50 million and it is better than 0',
        directionAscending: true,
        interpretation: 'Accounts with an attachment point = >  € 50 million will obtain a higher ATS. Primary/Excess aims to favor the excess business. ',
        isDiscrete: true
    },
    {
        kpiKey: 'cyb_aptp',
        lobKey: 'CYB',
        attribute: 'aptp',
        position: 3,
        name: 'APTP',
        definition: 'Actual Price/Technical Price ',
        weight: 0.25,
        inputValue: 'Percentage',
        directionAscending: true,
        interpretation: 'Accounts with higher APTP  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'cyb_hazard_grade',
        lobKey: 'CYB',
        attribute: 'hazardGrade',
        position: 4,
        name: 'Hazard Grade',
        definition: 'Hazard Grade ',
        weight: 0.25,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Accounts with higher HG will obtain a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'fls_pi_aptp_',
        lobKey: 'FLS',
        attribute: 'piAptp',
        position: 1,
        name: 'PI APTP ',
        definition: 'Actual Price/Technical Price from pricing model',
        weight: 0.05,
        inputValue: 'Percentage',
        directionAscending: true,
        interpretation: 'Accounts with higher APTP  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'fls_pi_historical_ulr_',
        lobKey: 'FLS',
        attribute: 'piHistoricalUlr',
        position: 2,
        name: 'PI Historical ULR*',
        definition: 'Historical Ultimate Loss Ratio. Aggregate GN Ultimate LR over the last 5 or 10 UW years ',
        weight: 0.15,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with a higher ULR or ILR will obtain a lower ATS. *ULR when reliable, otherwise ILR.',
        isDiscrete: false
    },
    {
        kpiKey: 'fls_pi_hazard_grade',
        lobKey: 'FLS',
        attribute: 'piHazardGrade',
        position: 3,
        name: 'PI Hazard Grade',
        definition: 'Hazard grade',
        weight: 0.15,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Accounts with higher HG will obtain a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'fls_pi_us_exposure_',
        lobKey: 'FLS',
        attribute: 'piUsExposure',
        position: 4,
        name: 'PI US Exposure ',
        definition: 'Turnover US > 0 ',
        weight: 0.15,
        inputValue: '0 or 1, where 0 = Turnover US = 0 and 1 = Turnover US > 0, and 1 is less adequate than 0',
        directionAscending: false,
        interpretation: 'Accounts with positive Turnover in US will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'fls_do_historical_ulr_',
        lobKey: 'FLS',
        attribute: 'doHistoricalUlr',
        position: 5,
        name: 'DO Historical ULR*',
        definition: 'Historical Ultimate Loss Ratio. Aggregate GN Ultimate LR over the last 5 or 10 UW years ',
        weight: 0.05,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with a higher ULR or ILR will obtain a lower ATS. *ULR when reliable, otherwise ILR.',
        isDiscrete: false
    },
    {
        kpiKey: 'fls_do_aptp_',
        lobKey: 'FLS',
        attribute: 'doAptp',
        position: 6,
        name: 'DO APTP ',
        definition: 'Actual Price/Technical Price from pricing model',
        weight: 0.05,
        inputValue: 'Percentage',
        directionAscending: true,
        interpretation: 'Accounts with higher APTP  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'fls_do_hazard_grade',
        lobKey: 'FLS',
        attribute: 'doHazardGrade',
        position: 7,
        name: 'DO Hazard Grade',
        definition: 'Hazard grade',
        weight: 0.15,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Accounts with higher HG will obtain a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'fls_do_peu_exposure',
        lobKey: 'FLS',
        attribute: 'doPeuExposure',
        position: 8,
        name: 'DO PEU Exposure',
        definition: 'Primary/Excess/US-Aus Exposure defined by a matrix depending on Company Structure and Primary vs Excess - please see table below ',
        weight: 0.2,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Risky mix will receive an higher number (5) and a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'fls_do_us_market_cap_',
        lobKey: 'FLS',
        attribute: 'doPercentUsMarketCap',
        position: 10,
        name: 'DO % US market cap.',
        definition: '% value of market capitalization in the US',
        weight: 0.05,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'The higher the % US market capitalization the lowest the ATS obtained. % US market capitalization aims to penalize accounts with US market capitalization. ',
        isDiscrete: false
    },
    {
        kpiKey: 'mar_historical_ilr',
        lobKey: 'MAR',
        attribute: 'historicalIlr',
        position: 1,
        name: 'Historical ILR',
        definition: 'Historical Incurred Loss Ratio. Aggregate GN incurred LR over the last 5 or 10 UW years  ',
        weight: 0.6,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with higher Historical LR will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'mar_aptp',
        lobKey: 'MAR',
        attribute: 'aptp',
        position: 2,
        name: 'APTP',
        definition: 'Actual Price/Technical Price from pricing model',
        weight: 0.15,
        inputValue: 'Percentage',
        directionAscending: true,
        interpretation: 'Accounts with higher APTP  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'mar_hazard_grade',
        lobKey: 'MAR',
        attribute: 'hazardGrade',
        position: 3,
        name: 'Hazard Grade',
        definition: 'Hazard Grade  ',
        weight: 0.15,
        inputValue: '1, 2, 3, 4 or 5',
        directionAscending: false,
        interpretation: 'Accounts with higher HG will obtain a lower ATS',
        isDiscrete: true
    },
    {
        kpiKey: 'mar_dnv_hull_',
        lobKey: 'MAR',
        attribute: 'dnvHull',
        position: 4,
        name: 'DNV (Hull)',
        definition: 'DNV score',
        weight: 0.05,
        inputValue: 'From 0 to 100',
        directionAscending: true,
        interpretation: 'Accounts with higher DNV score  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'mar_aal_on_premium_cargo_',
        lobKey: 'MAR',
        attribute: 'aalOnPremiumCargo',
        position: 5,
        name: 'AAL on Premium (Cargo)',
        definition: 'Average Annual Large Losses. Modelled AAL as % of the GNWP',
        weight: 0.05,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with a higher AAL on Premium will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'par_expected_loss_ratio',
        lobKey: 'PAR',
        attribute: 'expectedLossRatio',
        position: 1,
        name: 'Expected Loss Ratio',
        definition: 'Account Expected Loss ratio, is calculated as the ratio of Losses (EL*Limit_net) and NWP. Is a proxy of the inverse of APTP',
        weight: 0.3,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with an higher Expected Loss Ration will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'par_volatility',
        lobKey: 'PAR',
        attribute: 'adjustedVolatility',
        position: 2,
        name: 'Volatility',
        definition: 'Represents the volatility of an account, is calculated as the ratio between OEP 250 (P99.6*Limit_net) and the Losses (EL*Limit_net)',
        weight: 0.5,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with an higher Volatility will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'par_diversification',
        lobKey: 'PAR',
        attribute: 'diversification',
        position: 3,
        name: 'Diversification',
        definition: 'Measures how the risk of a single account impacts the entire portfolio. It is calculated using the CAT loading from the parametric pricing model  normalized against the maximum possible loading, which serves as a reasonable proxy for diversification against the broader GC&C portfolio.',
        weight: 0.2,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with an higher Diversification will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'prp_aal_on_premium',
        lobKey: 'PRP',
        attribute: 'aalOnPremium',
        position: 1,
        name: 'AAL on Premium',
        definition: 'Average Annual Large Losses. Modelled AAL as % of the GNWP',
        weight: 0.15,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with a higher AAL on Premium will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'prp_aptp',
        lobKey: 'PRP',
        attribute: 'aptp',
        position: 2,
        name: 'APTP',
        definition: 'Actual Price/Technical Price from pricing model',
        weight: 0.2,
        inputValue: 'Percentage',
        directionAscending: true,
        interpretation: 'Accounts with higher APTP  will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'prp_adjusted_volatility',
        lobKey: 'PRP',
        attribute: 'adjustedVolatility',
        position: 3,
        name: 'Adjusted Volatility',
        definition: 'Adjusted Volatility measures the Net Volatility expected after considering the LL Margin absorption effect. ',
        weight: 0.2,
        inputValue: 'number',
        directionAscending: false,
        interpretation: 'When it is greater than 0, it indicates that the LL Margin is not sufficient to absorb the account volatility in the long run. Accounts with higher Volatility will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'prp_historical_ilr',
        lobKey: 'PRP',
        attribute: 'historicalIlr',
        position: 4,
        name: 'Historical ILR',
        definition: 'Historical Incurred Loss Ratio. Aggregate GN incurred LR over the last 5 or 10 UW years  ',
        weight: 0.2,
        inputValue: 'Percentage',
        directionAscending: false,
        interpretation: 'Accounts with higher Historical LR will obtain a lower ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'prp_oarr',
        lobKey: 'PRP',
        attribute: 'oarr',
        position: 5,
        name: 'OARR',
        definition: 'Overall Account Risk Rating as numeric score',
        weight: 0.25,
        inputValue: 'From 0 to 100',
        directionAscending: true,
        interpretation: 'Accounts with a higher OARR will obtain a higher ATS',
        isDiscrete: false
    },
    {
        kpiKey: 'avn_fac_reinsurance',
        lobKey: 'AVN',
        attribute: 'facReinsurance',
        position: null,
        name: 'FAC Reinsurance',
        definition: '0 = Fac is NOT present, 1 = Fac is present. No Fac  accounts will obtain a higher ATS. When Fac is present accounts will recieve lower ATS.',
        weight: 0.2,
        inputValue: '0 = Fac is NOT present, 1 = Fac is present. No Fac  accounts will obtain a higher ATS. When Fac is present accounts will recieve lower ATS.',
        directionAscending: false,
        interpretation: null,
        isDiscrete: true
    },
    {
        kpiKey: 'cas_multiline_relationship',
        lobKey: 'CAS',
        attribute: 'multilineRelationship',
        position: null,
        name: 'Multiline relationship',
        definition: ' numeric indicator which depends by domestic lobs',
        weight: 0.1,
        inputValue: '1 = client has no domestic lobs, 2 = client has any domestic lobs different from domestic general liability, 3 = client has domestic general liability',
        directionAscending: true,
        interpretation: null,
        isDiscrete: false
    },
    {
        kpiKey: 'mar_overall_client_gc_c_historical_ilr',
        lobKey: 'MAR',
        attribute: 'overallClientGcCHistoricalIlr',
        position: null,
        name: 'Overall Client GC&C Historical ILR',
        definition: 'Historical Incurred Loss Ratio aggregated at Cliente level',
        weight: null,
        inputValue: 'Historical Incurred Loss Ratio aggregated at Cliente level',
        directionAscending: false,
        interpretation: null,
        isDiscrete: false
    },
    {
        kpiKey: 'mar_historical_ilr_2yrs',
        lobKey: 'MAR',
        attribute: 'historicalIlr2yrs',
        position: null,
        name: 'Historical ILR 2yrs',
        definition: 'Historical Incurred Loss Ratio considering last 2yrs claims history',
        weight: 0.3,
        inputValue: 'Historical Incurred Loss Ratio considering last 2yrs claims history',
        directionAscending: false,
        interpretation: null,
        isDiscrete: false
    },
    {
        kpiKey: 'prp_mfl_tiv_ratio',
        lobKey: 'PRP',
        attribute: 'mflTivRatio',
        position: null,
        name: 'MFL / TIV ratio',
        definition: 'null',
        weight: null,
        inputValue: '',
        directionAscending: true,
        interpretation: null,
        isDiscrete: false
    },
    {
        kpiKey: 'prp_overall_client_gc_c_historical_ilr',
        lobKey: 'PRP',
        attribute: 'overallHistoricalIlr',
        position: null,
        name: 'Overall Client GC&C Historical ILR',
        definition: 'Historical Incurred Loss Ratio aggregated at Cliente level',
        weight: null,
        inputValue: 'Historical Incurred Loss Ratio aggregated at Cliente level',
        directionAscending: false,
        interpretation: null,
        isDiscrete: false
    }
];

export class DataDictionaryService {

    public static AccountDataDictionary: AccountDataAttribute[] = AccountDataDictionaryRaw;
    public static AccountDataDictionaryMap = new Map<string, AccountDataAttribute>(
        DataDictionaryService.AccountDataDictionary.map(attr => [attr.attribute, attr])
    );

    public static KpiDefinitions: KpiDefinition[] = KpiDefinitionsRaw;
    public static KpiDefinitionsMap = new Map<string, KpiDefinition>(
        DataDictionaryService.KpiDefinitions.map(kpi => [kpi.attribute, kpi])
    );
    public static KpiDefinitionsByKeyMap = new Map<string, KpiDefinition>(
        DataDictionaryService.KpiDefinitions.map(kpi => [kpi.kpiKey, kpi])
    );

    public static getAccountDataAttribute(attributeName: string | undefined | null): AccountDataAttribute | undefined {
        if (!attributeName) return undefined;
        return DataDictionaryService.AccountDataDictionaryMap.get(attributeName);
    }

    public static getAccountDataAttributeRequired(attributeName: string): AccountDataAttribute {
        const attr = DataDictionaryService.AccountDataDictionaryMap.get(attributeName);
        if (!attr) throw new Error(`Attribute ${attributeName} not found in account data dictionary`);
        else return attr;
    }

    public static getKpiDefinition(attributeName: string | undefined | null): KpiDefinition | undefined {
        if (!attributeName) return undefined;
        const attribute = DataDictionaryService.getAccountDataAttribute(attributeName);
        const kpiDefinition = DataDictionaryService.KpiDefinitionsMap.get(attributeName)!;
        return { ... kpiDefinition, format: attribute?.format };
    }

    public static isKpiDiscrete(attributeName: string): boolean {
        const kpi = DataDictionaryService.getKpiDefinition(attributeName);
        return kpi?.isDiscrete ?? false;
    }

    public static getKpiDefinitionRequired(attributeName: string): KpiDefinition {
        const kpi = DataDictionaryService.KpiDefinitionsMap.get(attributeName);
        if (!kpi) throw new Error(`KPI definition for attribute ${attributeName} not found`);
        else return kpi;
    }

    public static getKpiDefinitionByKey(kpiKey: string | undefined | null): KpiDefinition | undefined {
        if (!kpiKey) return undefined;
        return DataDictionaryService.KpiDefinitionsByKeyMap.get(kpiKey);
    }

    public static getKpiDefinitionByKeyRequired(kpiKey: string): KpiDefinition {
        const kpi = DataDictionaryService.KpiDefinitionsByKeyMap.get(kpiKey);
        if (!kpi) throw new Error(`KPI definition for key ${kpiKey} not found`);
        else return kpi;
    }

    public static kpiDirectionIcon(directionUp: boolean): string {
        return directionUp ?
            `<i class="fad fa-arrow-up-1-9" title="Higher is better"></i>` :
            `<i class="fad fa-arrow-down-9-1" title="Lower is better"></i>`;
    }

    public static getAccountDataAttributeJsonType(attr: string): JsonDataType {
        const dd = DataDictionaryService.getAccountDataAttributeRequired(attr);
        switch (dd.pfmDataType) {
            case 'text': return JsonDataType.String;
            case 'number': return JsonDataType.Number;
            case 'amount': return JsonDataType.Number;
            case 'integer': return JsonDataType.Number;
            case 'percentage': return JsonDataType.Number;
            case 'date': return JsonDataType.Date;
            case 'datetime': return JsonDataType.DateTime;
            case 'boolean': return JsonDataType.Boolean;
            default: throw new Error(`Unknown data type for attribute ${attr}: ${dd.pfmDataType}`);
        }
    }


}
